<template>
    <div class="telekom-angebote-kombipakete">
        <BasicBreadcrumb
            :items="[
                { label: 'Telekom Angebote', to: `/markt/${storeslug}/telekom-angebote` },
                { label: 'Kombi-Pakete', to: '' }
            ]"
        />
        <div class="lg:container mx-auto p-8 lg:px-0 tariffSlider">
            <ContentHeroBanner
                v-for="banner in heroBanner?.items" :key="banner?.id"
                :img-mobile="banner?.imageUrlMobile"
                :img-desktop="banner?.imageUrl"
                :title="banner?.title"
                :background-img-mobile="backgroundImgMobile"
                :background-img-desktop="backgroundImgDesktop"
            />
            <div class="py-2">
                <h1 class="pt-8 pb-4 container mx-auto font-bold text-paragraph text-2xl text-center">
                    Unsere aktuellen TOP-ANGEBOTE der Telekom -<br>
                    vor Ort im Markt für Sie verfügbar
                </h1>
                <div class="text-2xl text-center">
                    Die neuen Pluskarten 2.0 sind da<br>
                    Die neuen PlusKarten der Telekom bieten jetzt über die Vererbungslogik ebenfalls Sorgenfreiheit.
                </div>
            </div>
            <div v-if="offer?.items?.length" class="mt-4">
                <div v-for="offerCombi in offer.items" :key="offerCombi.id">
                    <div class="hidden sm:flex sm:items-center w-full rounded-3xl bg-center">
                        <img 
                            class="w-full" 
                            :src="offerCombi.imageUrlDesktop" 
                            :alt="offerCombi.title" 
                            :title="offerCombi.title"
                        >
                    </div>         
                    <div v-if="offerCombi.text" class="legalText py-8 text-xs">
                        <div class="text-primary text-center pt-4 more cursor-pointer" @click="toggleLegalText">
                            {{ showLegalText ? '− Rechtstexte einklappen' : '+ Rechtstexte ausklappen' }}
                        </div>
                        <div v-if="showLegalText" class="flex flex-col gap-4 pt-4">
                            <div class="whitespace-pre-wrap">{{ offerCombi.text }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="py-2">
                <BasicBtErrorBoundary>
                    <TariffSlider
                        :key="Date.now()"
                        :tenant-id="tenantId"
                        :sub-tenant="false"
                        :plus="true"
                        :title="'<strong>PlusKarten für Familie und Freunde</strong>'"
                        :store="currentStore"
                    />
                </BasicBtErrorBoundary>
            </div>     
            <div class="pt-4 pb-12 container mx-auto font-bold text-paragraph text-2xl text-center">
                Alle Informationen und Angebote zu den Themen Mobilfunk,<br>
                MagentaTV, Internet und Glasfaser findest du hier
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 pb-8">
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconContract"
                    url="/telekom-angebote/mobilfunk"
                    :title="'Handy mit Vertrag'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconMagenta"
                    url="/telekom-angebote/magentatv"
                    :title="'MagentaTV'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconInternet"
                    url="/telekom-angebote/internet"
                    :title="'Internet'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconOptical"
                    url="/telekom-angebote/glasfaser"
                    :title="'Glasfaser'"
                />
            </div>
        </div>
        <TariffEyeCatcher/>
    </div>
</template>

<script setup lang="ts">

    import type { ContentBlock } from '~~/models/Tariff/ContentBlock';
    import { ContentBlockType } from '~~/models/Tariff/ContentBlock';

    import backgroundImgMobile from '~/assets/img/tariff-topic/telekom/ssb_BG_mobile.png';
    import backgroundImgDesktop from '~/assets/img/tariff-topic/telekom/ssb_BG.png';
    import backgroundTile from '~/assets/img/tariff-topic/telekom/bg_icons.png';
    import iconContract from '~/assets/img/tariff-topic/telekom/two/mobilfunk.png';
    import iconMagenta from '~/assets/img/tariff-topic/telekom/two/magentaTV.png';
    import iconInternet from '~/assets/img/tariff-topic/telekom/two/internet.png';
    import iconOptical from '~/assets/img/tariff-topic/telekom/two/glasfaser.png';

    const tenantId: string = "692ef56ad1b19b630ac62ff3";
    const currentStore = useCurrentStore();

    const route = useRoute();
    const storeslug = route.params.storeslug;

    const storeName = currentStore?.value?.name ?? 'expert';
    const pageTitle = 'Telekom Kombipakete bei ' + storeName + ' | MagentaEINS Angebote bei expert';
    const pageDescription = 'Telekom Kombipakete bei ' + storeName + ': MagentaEINS mit Internet, Mobilfunk & TV kombinieren und Vorteile sichern. Jetzt Angebot entdecken!';
    useHead({
        title: pageTitle,
        meta: [{ name: 'description', content: pageDescription }
        ]
    });
    
    const fetchContentBlock = (type: ContentBlockType) =>
    useFetch<{ items: ContentBlock[] }>(
        `/api/nea/tariff-service/contentBlock`,
        {
            query: {
                page: 0,
                validOnly: true,
                pageSize: 1,
                type
            }
        }
    );

    const { data: heroBanner } = await fetchContentBlock(ContentBlockType.TELEKOM_COMBI_BANNER);
    const { data: offer } = await fetchContentBlock(ContentBlockType.TELEKOM_COMBI_OFFER);
 
    const showLegalText = ref(false);
    const toggleLegalText = () => {
        showLegalText.value = !showLegalText.value;
    };    

    await checkStoreHasPage(tenantId);

    watch(currentStore, async (store) => {
        const storeId: string = store?.id;
        if (!storeId) {
            return;
        }
        await checkStoreHasPage(tenantId);
    });    
</script>